import { Component, Input, Output, EventEmitter, OnInit, inject, OnChanges, SimpleChanges, AfterViewInit, signal } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ViewNewRepApplication } from '../../new-rep-application-interfaces/viewnew-rep-application.interface';

@Component({
  selector: 'app-declaration-clauses',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  templateUrl: './declaration-clauses.component.html',
  styleUrl: './declaration-clauses.component.scss'
})
export class DeclarationClausesComponent implements OnInit {

  @Input() formGroup!: FormGroup;
  @Input() declarationClauses!: ViewNewRepApplication;
  @Input() message = '';
  @Input() readonly = signal(false);
  @Output() next = new EventEmitter<string[]>();

  private fb = inject(FormBuilder);
  status = 'Not Started';
  isInitialLoad = true;
  declarations: string[] = [
    `I declare that I have resigned from my previous employment(s) unless a gainful employment has been submitted. The information given in this application is up-to-date and in the attached annexes (if any) are true and correct. I understand that any misrepresentation or omission of facts will be sufficient cause to render my application void. The validity of my declaration is 3 months from the date of my signed application form.`,

    `I am aware that under Section 23L of the Financial Advisers Act, an individual who has provided a statement that is materially false or misleading to his principal and the statement is subsequently lodged with the Monetary Authority of Singapore, or has omitted to give information to his principal which causes the lodged document to be misleading in a material respect, shall be guilty of an offence and shall be liable on conviction to a fine not exceeding $50,000.`,

    `I understand that my application is subject to the acceptance and approval of both MAS and PIAS. I am fully aware that both MAS and PIAS have the rights to reject my application at their sole discretion without assigning any reasons directly to me. Should my application be accepted and approved, I understand that the nature of appointment with PIAS is on a self-employed arrangement, unless otherwise, communicated in writing by the Company.`,

    `I have read and understood all information contained in (a) Guidelines No. FSG-G01 - Guidelines on Fit and Proper Criteria and (b) Guidelines No. FAA-G04 - Guidelines on Standards of Conduct for Financial Advisers and Representatives ("Guidelines"); and in submitting this form, I am satisfied that I am a fit and proper person based on the criteria stated in the Guidelines.`,

    `I shall abide by the MAS Notices, Central Provident Fund (CPF) Board Guidelines and all rules and regulations issued by PIAS from time to time.`,

    `Under the MAS Representative Notification Framework, I understand that it is my duty to inform PIAS of any changes to the above declaration within 7 calendar days of the change. It is noted that under the regulations, any person who, without reasonable excuse, contravenes the above shall be guilty of an offence.`,

    `I understand and agree that I will not conduct any financial advisory service (as defined by the Financial Advisers Act) including without limitation, any form of sales activities, until I have been issued the Rep Code by PIAS, even if my Representative Number has been issued by the MAS.`,

    `A link to complete Induction Program will be sent to me after a full application pack is received by AMU and I can proceed to complete the Induction Program. Validity of the Induction Program is 3 months from the completion date. Should my RNF not approved after the 3 months, I agree to re-do the whole Induction Program.`,

    `I understand that reference check(s) has/have not been performed with my past employer(s) before I attend the Induction Program. By attending the Induction Program, it does not imply/guarantee the approval of my FA representative licence.`,

    `I understand that in the event that I have completed all or part of the Induction Program and my application is rejected by PIAS and/or the MAS (as stated in point 3), I cannot claim compensation from PIAS on the time loss spent in completing all or part of the Induction Program.`,

    `I shall treat as confidential and keep secret all information in connection thereto conveyed to me through any meetings, materials, seminars or (induction) training provided to me by PIAS or any party designated by PIAS. The foregoing obligations as to confidentiality shall remain in full force and effect notwithstanding my withdrawal or the non-approval of this application.`,

    `I hereby give consent to PIAS to use my personal data for the purpose of managing my engagement and/or working relationship with PIAS; and carrying out due diligence, monitoring or other screening activities in accordance with PIAS’ legal or regulatory obligations or risk management procedures.`,

    `I understand and agree to compensate the MAS lodgement fees and any other costs (being the recovery cost incurred by the Company during the on-boarding process) if my contract agreement is terminated within the first year of my service/time with PIAS (regardless voluntarily or involuntarily). In addition, the Company is entitled to be compensated if I have outstanding dues under any financing/transition allowance, unsecured loans, dues and/or other ownings which has been incurred prior to the on-boarding and/or during my period of service with the Company.`
  ];

  async ngOnInit(): Promise<void> {
    this.formGroup.addControl('declarationAccepted', this.fb.control(null, [Validators.required]));
    this.formGroup.addControl('declarantName', this.fb.control(''));
    this.formGroup.addControl('declarationDate', this.fb.control(''));


    await this.fetchDeclarationClause();
    this.handleFormStatus();

  }

  fetchDeclarationClause(): void {
    if (this.declarationClauses.isAgreed) {
      this.formGroup.controls['declarationAccepted']?.setValue(this.declarationClauses.isAgreed);
      this.formGroup.controls['declarantName']?.setValue(this.declarationClauses.declarationFullName);
      if (this.declarationClauses?.declarationDate !== '1900-01-01T00:00:00Z') {
        this.formGroup.controls['declarationDate']?.setValue(this.declarationClauses.declarationDate.split('T')[0]);
      }
    }

    if (this.formGroup.valid) {
      this.status = 'Completed';
      this.next.emit([this.status, 'submitted']);
    } else if (this.formGroup.invalid) {
      const anyFilled = Object.values(this.formGroup.value).some(v => v && v.toString().trim() !== '');
      if (anyFilled) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'submitted']);
      } else {
        this.status = 'Not Started';
        this.next.emit([this.status, 'submitted']);
      }
    }
    // Silent: this runs after the status was emitted above, so an announcement
    // here would reach the status watcher as a user edit and downgrade a
    // completed section to "In Progress" the moment it loaded.
    if (this.readonly() == true) {
      this.formGroup?.disable();
      this.formGroup.controls['declarationDate']?.setValue(this.declarationClauses.declarationDate.split('T')[0], { emitEvent: false });
    } else {
      this.formGroup?.enable();
    }

  }


  handleFormStatus() {
    if (this.readonly()) return;

    const initialValue = this.formGroup.getRawValue();

    this.formGroup.valueChanges.subscribe(currentValue => {
      const hasChanged =
        JSON.stringify(initialValue) !== JSON.stringify(currentValue);

      if (hasChanged) {
        this.status = 'In Progress';
        this.next.emit([this.status, 'change']);
      }
    });

    this.formGroup.get('declarationAccepted')?.valueChanges.subscribe(value => {
      if (value) {
        this.formGroup.patchValue({
          declarantName: this.declarationClauses?.fullNameNric,
          declarationDate: this.getToday()
        });
        this.message = '';
      } else if (value == false) {
        this.formGroup.patchValue({
          declarantName: null,
          declarationDate: null
        });
        this.formGroup.markAllAsTouched();
      }
    });
  }

  getToday(): string {
    return new Date().toISOString().split('T')[0];
  }

  submit(): void {

    if (this.formGroup.valid) {
      this.status = 'Completed';

      this.next.emit([this.status, 'finalsubmit']);
    }
    this.formGroup.markAllAsTouched();
  }
}

